import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Ticket, Lock, LayoutDashboard } from "lucide-react";
import { useI18n } from "../i18n";
import { useAuth } from "../auth";

const NAV_LINKS = [
  { href: "#tour", key: "tour" },
  { href: "#about", key: "about" },
  { href: "#videos", key: "videos" },
  { href: "#gallery", key: "gallery" },
  { href: "#press", key: "press" },
  { href: "#contact", key: "contact" },
];

const LangToggle = () => {
  const { lang, setLang } = useI18n();
  return (
    <div
      className="inline-flex items-center border border-white/15 text-xs font-semibold tracking-widest"
      data-testid="lang-toggle"
    >
      <button
        onClick={() => setLang("ru")}
        data-testid="lang-ru"
        className={`px-3 py-1.5 transition-colors ${
          lang === "ru" ? "bg-[#FFDE00] text-black" : "text-white/70 hover:text-white"
        }`}
      >
        RU
      </button>
      <button
        onClick={() => setLang("en")}
        data-testid="lang-en"
        className={`px-3 py-1.5 transition-colors ${
          lang === "en" ? "bg-[#FFDE00] text-black" : "text-white/70 hover:text-white"
        }`}
      >
        EN
      </button>
    </div>
  );
};

const Header = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-black/70 border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="logo"
          className="flex items-center gap-2 group"
        >
          <span className="w-2 h-2 bg-[#FFDE00] block group-hover:scale-150 transition-transform" />
          <span className="font-display text-2xl md:text-3xl tracking-tight">
            ASTANA<span className="text-[#FFDE00]">.</span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.key}
              href={l.href}
              data-testid={`nav-${l.key}`}
              className="text-sm font-medium tracking-wide text-white/70 hover:text-white transition-colors uppercase"
            >
              {t.nav[l.key]}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <LangToggle />
          {user ? (
            <Link to="/admin" className="btn-outline" data-testid="header-admin-link">
              <LayoutDashboard size={14} /> {t.nav.admin}
            </Link>
          ) : (
            <Link to="/login" className="hidden lg:inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-[#FFDE00] uppercase tracking-widest font-semibold" data-testid="header-login-link">
              <Lock size={12} /> {t.nav.login}
            </Link>
          )}
          <a href="#tour" className="btn-primary" data-testid="header-cta-tickets">
            <Ticket size={16} /> {t.nav.tickets}
          </a>
        </div>

        <button
          className="lg:hidden text-white"
          onClick={() => setOpen(true)}
          data-testid="mobile-menu-open"
          aria-label="Open menu"
        >
          <Menu size={26} />
        </button>
      </div>

      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 z-[60] flex flex-col overflow-hidden"
          data-testid="mobile-menu"
        >
          {/* Cinematic background */}
          <div className="absolute inset-0">
            <img
              src="https://i.wfolio.ru/x/Sjpgrm2v20FR6Cth5viRk6Iir5aoqG4h/ZXWGJmu7EQlhnv6D9ELHSMzGOBuFicXp/OGcM34slpvmXTMkbuJagSV1xA4akZdWt/iGTrK_jx2PJdzPpMyVlbGIyR5mAu9UqG/rL-5DUxboiaoOuXyk54XGX-OT-NQ5DIt/t4cadjULppTx1gEZEyG7Eqk7p-JphqNA/E-VjBOssplpcT44FqigxaffjYT-JHK8i/mHgOpNBV6NjofJOp8brBCIX8mnUYkkzn/2-Rxx5zPZzrrjfptWNwJfI7uAOH4gL5v/9qwYf3zFfDh4JPGxZVTjZFyhs4Bz5IfD/Pf7r1692GFuCX4TTam2YgfIRxxiiU6ZK/gaUOHomJKN04GwZ_44Ya6fm8RLXvxLQP/yptMSd_P6UUdLpmuc53KlsFYQGx2Itbz/Wxuhy1hppYfBoz7eYXxsuH8fbi6CefNF/enxa4ZnED7b_2cn9uvOGyDYIknIkmavI/2mMcRVvpmbcB3ck8ienXOUnQ6ZSnRkv-/5vcphuD5YXAoi9d0rYR0NgGtaX2NiQxR/mV1s-HQ7_ek.jpg"
              alt=""
              className="w-full h-full object-cover"
              referrerPolicy="no-referrer"
            />
            <div className="absolute inset-0 bg-black/85" />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(ellipse 60% 50% at 70% 20%, rgba(225,29,46,0.30) 0%, transparent 60%)," +
                  "radial-gradient(ellipse 70% 60% at 20% 80%, rgba(225,29,46,0.22) 0%, transparent 60%)," +
                  "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(10,0,2,0.85) 100%)",
              }}
            />
          </div>

          <div className="relative z-10 flex items-center justify-between px-6 h-16 border-b border-white/15 backdrop-blur-md bg-black/30">
            <span className="font-display text-2xl tracking-tight text-white">
              ASTANA<span className="text-[#FFDE00]">.</span>
            </span>
            <button
              onClick={() => setOpen(false)}
              data-testid="mobile-menu-close"
              aria-label="Close menu"
              className="text-white hover:text-[#FFDE00] transition-colors"
            >
              <X size={28} />
            </button>
          </div>

          <nav className="relative z-10 flex-1 flex flex-col items-start justify-center gap-5 px-8">
            {NAV_LINKS.map((l, i) => (
              <a
                key={l.key}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.key}`}
                className="font-display text-5xl tracking-tight text-white hover:text-[#FFDE00] transition-colors"
                style={{
                  textShadow: "0 2px 16px rgba(0,0,0,0.6)",
                  animation: `mobileMenuIn 0.4s ease ${0.05 * i}s both`,
                }}
              >
                {t.nav[l.key]}
              </a>
            ))}
            <div className="mt-8 flex items-center gap-4 flex-wrap">
              <LangToggle />
              {user ? (
                <Link
                  to="/admin"
                  onClick={() => setOpen(false)}
                  className="btn-outline"
                  data-testid="mobile-admin-link"
                >
                  <LayoutDashboard size={14} /> {t.nav.admin}
                </Link>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  className="btn-outline"
                  data-testid="mobile-login-link"
                >
                  <Lock size={14} /> {t.nav.login}
                </Link>
              )}
              <a
                href="#tour"
                onClick={() => setOpen(false)}
                className="btn-primary"
                data-testid="mobile-cta-tickets"
              >
                <Ticket size={16} /> {t.nav.tickets}
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
